import { ButtonGroup, LinkButton } from "lib/miolo";
import classNames from "components/ui/classNames";
import { Button } from "components/ui/Button";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { Form, FormField, FormMessage } from "components/ui/Form";
import { FormattedMessage, useIntl } from "react-intl";
import CommentInputTextArea from "../CommentInputTextArea";
import type { CommentInputFormData } from "./shapes/commentInputFormDataShape";
import commentInputFormDataShape from "./shapes/commentInputFormDataShape";
import translateServerErrorIntoFriendlyMessage from "./translateServerErrorIntoFriendlyMessage";

interface IIndividualCommentInputFormProps {
  bodyAsPlainText?: string;
  submitButtonText: string;
  autoFocus?: boolean;
  onCancel: () => void;
  onSubmit: (bodyAsPlainText: string) => Promise<any>;
}

export default function IndividualCommentInputForm({
  bodyAsPlainText = "",
  submitButtonText,
  autoFocus,
  onCancel,
  onSubmit,
}: IIndividualCommentInputFormProps) {
  const intl = useIntl();
  const form = useForm<CommentInputFormData>({
    resolver: zodResolver(commentInputFormDataShape),
    defaultValues: {
      bodyAsPlainText,
    },
  });

  const {
    formState: { isSubmitting },
  } = form;

  const handleSubmit = async ({ bodyAsPlainText }: CommentInputFormData) => {
    try {
      await onSubmit(bodyAsPlainText);
    } catch (err) {
      form.setError("root.serverError", {
        message: translateServerErrorIntoFriendlyMessage(
          err,
          intl,
          intl.formatMessage({
            defaultMessage: "Não foi possível enviar sua mensagem agora. Tente novamente mais tarde.",
            id: "AsTfN9",
          }),
        ),
      });
    }

    return null;
  };

  return (
    <Form {...form}>
      <form onSubmit={form.handleSubmit(handleSubmit)} className="grid gap-y-5">
        <FormField
          control={form.control}
          name="bodyAsPlainText"
          render={({ field }) => {
            return (
              <div className="w-full space-y-2">
                <CommentInputTextArea
                  {...field}
                  autoFocus={autoFocus}
                  onKeyDown={(event) => {
                    if (event.key === "Enter" && (event.metaKey || event.ctrlKey)) {
                      form.handleSubmit(handleSubmit)();
                    }
                  }}
                  placeholder={intl.formatMessage({
                    defaultMessage: "Escreva alguma coisa aqui...",
                    id: "qr8o9T",
                  })}
                />

                {field.value && <FormMessage />}
                <FormMessage asRootError={true} />
              </div>
            );
          }}
        />

        <div className="grid grid-cols-2 items-center">
          <div className="flex justify-end font-normal">
            <ButtonGroup stack="horizontally" spacing={20}>
              <LinkButton
                disabled={isSubmitting}
                variant="black"
                onClick={() => {
                  form.reset();
                  onCancel();
                }}
              >
                <FormattedMessage defaultMessage="Cancelar" id="nZLeaQ" />
              </LinkButton>

              <Button
                className={classNames(
                  "flex h-9 w-auto select-none items-center justify-center overflow-visible rounded border-none",
                  "bg-brand px-3 text-sm text-white shadow-md hover:bg-brand/50 focus:outline-none",
                  {
                    "cursor-default bg-brand/50": isSubmitting,
                  },
                )}
                type="submit"
                disabled={isSubmitting}
              >
                {isSubmitting ? <FormattedMessage defaultMessage="Aguarde..." id="VZazm7" /> : submitButtonText}
              </Button>
            </ButtonGroup>
          </div>
        </div>
      </form>
    </Form>
  );
}
